<template>
	<div @keydown.esc="closeModals()" class="group-media-streams-wrapper">
		<div
			class="noMedia"
			v-if="noMedia"
			v-t="'groupsettings_mediatray_meetings_none'"
		/>
		<div width="100%">
			<div class="media-streams" @scroll="bottomVisible">
				<MediaItem
					v-for="message in items.filter(lsStoppedMessage)"
					class="stream-row"
					:item="message"
					:key="message.id"
					:messageId="message.id"
				>
					<MeetingEndedCard
						:title="$t('groupsettings_mediatray_meetings_meeting')"
						:videosession="message.meeting"
						:host="message.meeting.host"
						@replay="replayArchives(message)"
					>
						<template #profiles="{ userIds }">
							<Tippy
								:content="getTipContent(message.meeting)"
								:options="{
									animation: 'scale',
									duration: [100, 100],
									arrow: true,
									theme: 'bluewhite',
								}"
							>
								<ProfileList :userIds="userIds" :max="10" condensed />
							</Tippy>
						</template>
					</MeetingEndedCard>

					<template v-slot:date>
						{{ dateFormat(message.meeting.created_at) }}
					</template>
				</MediaItem>
			</div>

			<IosSpinner v-if="showMoreSpinner" bottom="0" left="50%" />
		</div>

		<SimpleModal v-if="showArchives" @close="showArchives = false">
			<div class="archives-wrapper">
				<div class="new-team-wrapper">
					<div
						class="upload-title"
						v-t="'chat_livestream_recordingspicker_title'"
					/>
				</div>
				<div>
					<div class="settings-list-small" style="max-height: 175px">
						<div
							v-for="archive in reversedArchives(currentMessage)"
							:key="archive.id"
							@click="playArchive(archive)"
							id="listRow"
						>
							<span style="padding-left: 10px">{{ archiveTime(archive) }}</span>
							<div id="playArrow"></div>
						</div>
					</div>
				</div>
			</div>
		</SimpleModal>

		<IosSpinner v-if="showSpinner" top="47%" left="calc(49vw - 125px)">
			<template #title>{{ $t('common_loader_loading_title') }}</template>
		</IosSpinner>
	</div>
</template>

<script>
import moment from 'moment'
import store, { getUser } from '@/store'
import { fetchHistoryWithoutStore } from '@/api/chat'
import { normalizeMessage } from '@/store/messages'
import { getCancelToken } from '@/helpers/api'
import IosSpinner from '@/components/IosSpinner'
import { SimpleModal, Tippy } from '@/components/common'
import MediaItem from '@/components/GroupSettings/MediaItem'
import ProfileList from '@/components/Message/ProfileList'
import MeetingEndedCard from '@/components/Message/MeetingEndedCard'
import { dateFormat } from '@/helperMethods'
import i18n from '@/i18n'

export default {
	name: 'GroupMediaStreams',
	components: {
		IosSpinner,
		SimpleModal,
		MediaItem,
		ProfileList,
		Tippy,
		MeetingEndedCard,
	},
	props: { chatId: [String, Number] },
	data() {
		return {
			store,
			bubble_id: null,
			loadHistoryCancelToken: null,
			showSpinner: false,
			showMoreSpinner: false,
			showArchives: false,
			streamsEl: null,
			noMedia: false,
			currentMessage: null,
			everythingLoaded: false,
			loadingMore: false,
			latest: null,
			items: [],
		}
	},

	mounted() {
		this.bubble_id = this.chatId || this.$route.params.id
		this.streamsEl = document.querySelector('.media-streams')
		this.getMessageHistory()
	},

	methods: {
		dateFormat,

		closeModals() {
			this.showArchives = false
		},

		duration(message) {
			let start = moment(message.meeting.created_at)
			let end = moment(message.meeting.ended_at)
			if (start.isValid() && end.isValid()) {
				return moment.duration(end.diff(start)).humanize()
			} else {
				return ''
			}
		},

		lsStoppedMessage(message) {
			return message.meeting.status !== 'ACTIVE'
		},

		getTipContent(meeting) {
			if (!meeting?.users) return ''
			return meeting.users
				.map(getUser)
				.map((u) => u.fullname)
				.join(', ')
		},

		reversedArchives(message) {
			return message.meeting.archives
		},

		archiveTime(archive) {
			let time = archive.duration
			let key = 'accessibility_seconds_label.variable'
			if (archive.duration > 60 && archive.duration < 3600) {
				time = Math.floor(archive.duration / 60)
				key = 'accessibility_minutes_label.variable'
			} else if (archive.duration > 3600) {
				time = Math.floor(archive.duration / 3600)
				key = 'accessibility_hours_label.variable'
			}
			return `${moment(archive.starttime + 'Z')
				.local()
				.format('LT')} (${i18n.global.t(key, { Count: time }, time)})`
		},

		replayArchives(message) {
			this.currentMessage = message
			if (message.meeting.archives.length > 1) {
				this.showArchives = true
			} else {
				this.showArchives = false
				const url = this.store.mediaPath(message.meeting.archives[0])
				this.$videoModal(url, 'meeting_replay.mp4')
			}
		},

		playArchive(archive) {
			this.showArchives = false
			this.$videoModal(this.store.mediaPath(archive), 'meeting_replay.mp4')
		},

		bottomVisible() {
			if (
				this.streamsEl.scrollTop + this.streamsEl.clientHeight >=
					this.streamsEl.scrollHeight &&
				this.everythingLoaded == false
			) {
				this.showMoreSpinner = true
				this.loadMoreMessages()
			}
		},

		loadMoreMessages() {
			this.getMessageHistory(this.latest)
		},

		getMessageHistory(latest) {
			if (!this.loadingMore) {
				if (this.latest == null) {
					this.showSpinner = true
				}
				this.loadingMore = true

				let data = { videosessions: true, bubble_id: this.bubble_id }

				if (latest) {
					data.latest = latest
				}

				if (this.loadHistoryCancelToken) {
					this.loadHistoryCancelToken.cancel()
					this.loadHistoryCancelToken = null
				}

				this.loadHistoryCancelToken = getCancelToken()

				fetchHistoryWithoutStore(data, this.loadHistoryCancelToken)
					.then((data) => {
						this.showSpinner = false
						this.loadingMore = false

						if (data.messages.length == 0) {
							this.noMedia = true
							this.everythingLoaded = true
						} else {
							this.showMoreSpinner = false
							this.items = this.items
								.concat(data.messages.map(normalizeMessage))
								.map((message) => {
									return {
										...message,
										user: message.meeting.host,
										created_at: message.meeting.created_at,
									}
								})

							if (data.pagesize === data.messages.length) {
								this.latest = Math.min(
									...data.messages.map((message) => message.id)
								)
							} else {
								this.everythingLoaded = true
							}
						}
					})
					.catch((e) => {
						console.error(e)
					})
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.media-streams {
	max-height: calc(100vh - 285px);
	overflow-y: auto;
	padding-bottom: 40px;
	padding-right: 15px;
	:deep(.media-item) {
		height: 82px !important;
	}
	:deep(.meeting-card-wrapper) {
		border: 0 !important;
	}
}

.stream-row {
	margin-bottom: 10px;
	border-radius: 8px;
	height: 60px;
	border: solid 1px #e0e0e0;
	padding: 0;
	display: flex;
	cursor: pointer;
}

.archives-wrapper {
	width: 450px;
	padding: 0 50px 50px 50px;
}

.stream-duration {
	white-space: nowrap;
	font-family: Avenir-Medium;
	font-size: 12px;
	color: #8a8d93;
	margin-left: 14px;
}

.stream-title {
	margin-left: 44px;
	margin-left: 14px;
	font-family: Avenir-Medium;
	font-size: 14px;
	color: #2c2d30;
}

.stream-icon-wrapper {
	padding-top: 22px;
	padding-left: 14px;
	padding-right: 14px;
	height: 60px;
	margin: -1px;
	border-top-left-radius: 8px;
	border-bottom-left-radius: 8px;
	width: 45px;
	background-color: #8a8d93;
}

.noMedia {
	position: absolute;
	top: 46%;
	left: calc(46% - 125px);
	width: 380px;
	font-family: Avenir-Medium;
	font-size: 28px;
	color: #4f5053;
	text-align: center;
}

.stream-replay {
	border-radius: 5px;
	height: 38px;
	background-color: #edeef0;
	font-family: Avenir-Medium;
	font-size: 14px;
	line-height: 0.71;
	letter-spacing: normal;
	text-align: center;
	color: #2c2d30;
	padding: 15px;
}

:deep(.left) {
	width: 328px;
	flex: unset;
}

:deep(.right) {
	flex: 1;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.group-media-streams-wrapper {
		.ios-spinner {
			background: var(--neutral-100) !important;
		}
	}
}
</style>
